import {
  compact,
  difference,
  forEach,
  intersection,
  isEmpty,
  isFunction,
  isUndefined,
  map,
  noop,
  omit,
} from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { ResizeCallbackData } from "react-resizable";
import { ExportField } from "types/api/exportField";
import {
  deleteLocalStorage,
  getLocalStorage,
  setLocalStorage,
} from "utils/localStorageUtils";

import { ACTableColumnType } from "ac-components/ACTable/ACTable";

import useUpdateEffect from "./useUpdateEffect";

export type ColumnSizeConfig = { [key: React.Key]: number };
export type ColumnOrderConfig = React.Key[];
export type ColumnVisibilityConfig = { [key: React.Key]: boolean };

export type ColumnSizeChangeCallback = (
  e: React.SyntheticEvent,
  data: ResizeCallbackData,
) => void;
export type ColumnOrderChangeCallback = (
  fromIndex: number,
  toIndex: number,
) => void;
export type ColumnVisibilityChangeCallback = (
  key: React.Key,
  visibility: boolean,
) => void;
export type UpdateConfigsFromCallback = (exportFields: ExportField[]) => void;

export type TableCustomizationHelperParams<Record> = {
  columns: ACTableColumnType<Record>[];
  draggable?: boolean;
  alwaysVisibleKeys?: React.Key[];
  defaultHiddenKeys?: React.Key[];
  prefKey: string;
  skipLocalStorage?: boolean;
  columnSizeConfig?: ColumnSizeConfig;
  skipColumnVisibility?: boolean;
  /** Admin-configured override default column keys (from tenant config) */
  overrideDefaultColumns?: string[];
};

export type TableCustomizationHelper<Record> = {
  columnSizeConfig: ColumnSizeConfig;
  columnVisibilityConfig: ColumnVisibilityConfig;
  onColumnSizeChange: ColumnSizeChangeCallback;
  columns: ACTableColumnType<Record>[];
  onColumnOrderChange: ColumnOrderChangeCallback;
  onColumnVisibilityChange: ColumnVisibilityChangeCallback;
  onResetColumnOptions(): void;
  columnOrderConfig: ColumnOrderConfig;
  exportColumns: ExportField[];
  updateConfigsFrom: UpdateConfigsFromCallback;
  draggableColumns: ColumnOrderConfig;
  alwaysVisibleKeys: React.Key[];
};

export const DummyCustomizationHelper: TableCustomizationHelper<any> = {
  columns: [],
  columnSizeConfig: {},
  columnVisibilityConfig: {},
  onColumnOrderChange: noop,
  onColumnSizeChange: noop,
  onColumnVisibilityChange: noop,
  onResetColumnOptions: noop,
  columnOrderConfig: [],
  exportColumns: [],
  updateConfigsFrom: noop,
  draggableColumns: [],
  alwaysVisibleKeys: [],
};

export default function useTableCustomizationHelper<Record>({
  columns,
  draggable,
  alwaysVisibleKeys = [],
  defaultHiddenKeys = [],
  prefKey,
  skipLocalStorage,
  columnSizeConfig: columnSizeConfigFromParams,
  skipColumnVisibility,
  overrideDefaultColumns,
}: TableCustomizationHelperParams<Record>): TableCustomizationHelper<Record> {
  if (process.env.NODE_ENV === "development") {
    const seenKeys = new Set<React.Key>();
    columns.forEach((c) => {
      if (!c.key) {
        throw new Error(
          "Column should have a key to support resize/reorder/show/hide",
        );
      }
      if (seenKeys.has(c.key)) {
        throw new Error(
          `Duplicate column key "${c.key}" found. Each column must have a unique key to support resize/reorder/show/hide`,
        );
      }
      seenKeys.add(c.key);
      if (!c.width) {
        throw new Error(`${c.key} column doesn't have any default width`);
      }
    });
  }

  const getInitialVisibilityConfig = useCallback(() => {
    // get the config from storage
    const existingPref = getVisibility(prefKey) || {};
    let visibility = skipLocalStorage ? {} : existingPref;

    // When admin override is active, columns NOT in the override list are hidden by default
    const effectiveHiddenKeys = overrideDefaultColumns
      ? columns
          .map((c) => c.key!)
          .filter(
            (key) =>
              !overrideDefaultColumns.includes(key as string) &&
              !alwaysVisibleKeys.includes(key),
          )
      : defaultHiddenKeys;

    // iterate over columns to add if it doesn't exist in config
    columns.forEach((c) => {
      const key = c.key!;
      if (alwaysVisibleKeys.includes(key)) {
        // if an always visible column exists in config, delete it
        return delete visibility[key];
      }
      // if a column doesn't exist in the existing config, then add it. it might be new column
      if (isUndefined(visibility[key])) {
        visibility[key] = !effectiveHiddenKeys.includes(key);
      }
    });
    // check if there are any keys in config which doesn't have a column
    // remove those extra keys from config
    const removedColumns = difference(
      Object.keys(visibility),
      columns.map((c) => c.key!),
    );
    if (removedColumns.length) {
      visibility = omit(visibility, removedColumns);
    }

    return visibility;
  }, [
    alwaysVisibleKeys,
    columns,
    defaultHiddenKeys,
    overrideDefaultColumns,
    prefKey,
    skipLocalStorage,
  ]);

  const [columnVisibilityConfig, setColumnVisibilityConfig] =
    useState<ColumnVisibilityConfig>(() => getInitialVisibilityConfig());

  const getInitialOrderConfig = useCallback(
    (initialVisibilityConfig = undefined) => {
      const existingPref = getOrder(prefKey) || [];
      let order = skipLocalStorage ? [] : existingPref;

      const visibilityConfig =
        initialVisibilityConfig || columnVisibilityConfig;

      // When admin override is active and no existing order, use override order
      if (overrideDefaultColumns && order.length === 0) {
        const columnKeySet = new Set(columns.map((c) => c.key!));
        const alwaysVisibleInOrder = columns
          .filter((c) => alwaysVisibleKeys.includes(c.key!))
          .map((c) => c.key!);
        const overrideKeys = overrideDefaultColumns.filter(
          (key) => !alwaysVisibleKeys.includes(key) && columnKeySet.has(key),
        );
        return [...alwaysVisibleInOrder, ...overrideKeys];
      }

      // iterating over columns to check for new added/removed columns
      columns.forEach((c) => {
        const key = c.key!;
        // if column is hidden then, check if it exists in order and remove it.
        if (!visibilityConfig[key] && !alwaysVisibleKeys.includes(key)) {
          if (order.includes(key)) {
            order.splice(order.indexOf(key), 1);
          }
          return;
        }
        if (order.includes(key)) {
          return;
        }
        if (!defaultHiddenKeys.includes(key)) {
          order.push(key);
        }
      });
      // check if there are any keys in config which doesn't have a column
      // remove those extra keys from config
      const allKeys = columns.map((c) => c.key!);
      const removedColumns = difference(order, allKeys);
      if (removedColumns.length) {
        order = intersection(allKeys, order);
      }
      return order;
    },
    [
      alwaysVisibleKeys,
      columnVisibilityConfig,
      columns,
      defaultHiddenKeys,
      overrideDefaultColumns,
      prefKey,
      skipLocalStorage,
    ],
  );

  const [columnSizeConfig, setColumnSizeConfig] = useState<ColumnSizeConfig>(
    () =>
      skipLocalStorage ? { ...columnSizeConfigFromParams } : getSize(prefKey),
  );
  const [columnOrderConfig, setColumnOrderConfig] = useState<ColumnOrderConfig>(
    () => getInitialOrderConfig(),
  );

  const onResetColumnOptions = useCallback(() => {
    deleteOrder(prefKey);
    deleteVisibility(prefKey);
    deleteSize(prefKey);

    const initialVisibilityConfig = getInitialVisibilityConfig();
    setColumnVisibilityConfig(initialVisibilityConfig);
    setColumnSizeConfig(getSize(prefKey));
    setColumnOrderConfig(getInitialOrderConfig(initialVisibilityConfig));
  }, [getInitialOrderConfig, getInitialVisibilityConfig, prefKey]);

  useUpdateEffect(() => {
    if (skipLocalStorage && columnSizeConfigFromParams) {
      setColumnSizeConfig(columnSizeConfigFromParams);
    }
  }, [columnSizeConfigFromParams, skipLocalStorage]);

  useUpdateEffect(() => {
    if (!skipLocalStorage) {
      setOrder(prefKey, columnOrderConfig);
    }
  }, [columnOrderConfig, skipLocalStorage]);
  useUpdateEffect(() => {
    if (!skipLocalStorage) {
      setVisibility(prefKey, columnVisibilityConfig);
    }
  }, [columnVisibilityConfig, skipLocalStorage]);
  useUpdateEffect(() => {
    if (!skipLocalStorage) {
      setSize(prefKey, columnSizeConfig);
    }
  }, [columnSizeConfig, skipLocalStorage]);

  const onColumnSizeChange: ColumnSizeChangeCallback = useCallback((e, d) => {
    if (d.size.width < 52 || d.size.width > 800) {
      return;
    }
    setColumnSizeConfig((prev) => ({
      ...prev,
      [d.node.dataset.key!]: d.size.width,
    }));
  }, []);

  const onColumnOrderChange: ColumnOrderChangeCallback = useCallback(
    (from, to) => {
      if (to === -1) return;
      setColumnOrderConfig((prev) => {
        const order = prev.slice(0);
        const key = order.splice(from, 1)[0];
        order.splice(to, 0, key);
        return order;
      });
    },
    [],
  );

  const onColumnVisibilityChange: ColumnVisibilityChangeCallback = useCallback(
    (key, visibility) => {
      setColumnVisibilityConfig((prev) => ({ ...prev, [key]: visibility }));
      setColumnOrderConfig((prev) => {
        if (visibility) {
          return [...prev, key];
        }
        if (prev.includes(key)) {
          const _newConfig = prev.splice(0);
          _newConfig.splice(_newConfig.indexOf(key), 1);
          return _newConfig;
        }
        return prev;
      });
      if (!visibility) {
        setColumnSizeConfig((prev) => omit(prev, key));
      }
    },
    [],
  );

  const { draggableColumns, orderedColumns: finalColumns } = useMemo(() => {
    const columnMap: { [key: React.Key]: ACTableColumnType<Record> } = {};
    columns.forEach((c) => {
      const key = c.key!;
      const isDraggable = draggable && !alwaysVisibleKeys.includes(key);
      const column = {
        ...c,
        width: columnSizeConfig[c.key!] || c.width,
        draggable: isDraggable,
        onHeaderCell: (column: ACTableColumnType<Record>) => {
          return {
            ...(isFunction(c.onHeaderCell) ? c.onHeaderCell(column) : {}),
            columnKey: column.key,
            width: column.width,
            onResize: onColumnSizeChange,
            draggable: isDraggable,
          };
        },
      };
      columnMap[column.key!] = column;
    });

    // When skipColumnVisibility is true, return all columns without filtering through columnOrderConfig
    const orderedColumns = skipColumnVisibility
      ? columns.map((c) => columnMap[c.key!])
      : compact(columnOrderConfig.map((key) => columnMap[key]));

    return {
      orderedColumns: orderedColumns,
      draggableColumns: orderedColumns
        .filter((c) => c.draggable)
        .map((c) => c.key!),
    };
  }, [
    columns,
    onColumnSizeChange,
    columnSizeConfig,
    draggable,
    columnOrderConfig,
    alwaysVisibleKeys,
    skipColumnVisibility,
  ]);

  const exportColumns = useMemo<ExportField[]>(
    () =>
      map(finalColumns, (entries) => {
        return {
          field: entries.key! as string,
          header: entries["title"] as string,
          width: columnSizeConfig[entries.key!] || undefined,
        };
      }),
    [finalColumns, columnSizeConfig],
  );

  const updateConfigsFrom = useCallback<UpdateConfigsFromCallback>(
    (exportColumns) => {
      const columnWidth: ColumnSizeConfig = {};
      const columnOrder: ColumnOrderConfig = [];
      const columnMap: { [key: React.Key]: ACTableColumnType<Record> } = {};
      columns.forEach((c) => {
        columnWidth[c.key!] = c.width as number;
        columnMap[c.key!] = c;
      });
      if (isEmpty(exportColumns)) {
        exportColumns = compact(
          map(columns, (column) => {
            const key = column.key as string;
            if (!defaultHiddenKeys.includes(key)) {
              return {
                field: key as string,
              };
            }
          }),
        );
      }
      forEach(exportColumns, (c) => {
        let key = c.field!;
        // Hack for findings custom columns
        if (key.startsWith("dynamicTags.")) {
          key = key.replace("dynamicTags", "keyValue");
        }
        if (!columnMap[key]) {
          return;
        }

        if (c.width) {
          columnWidth[key] = c.width;
        }
        columnOrder.push(key);
      });

      setColumnVisibilityConfig((prev) => {
        const newConfig = { ...prev };
        forEach(prev, (v, key) => {
          newConfig[key] = columnOrder.includes(key);
        });
        return newConfig;
      });
      setColumnOrderConfig(columnOrder);
      setColumnSizeConfig(columnWidth);
    },
    [columns, defaultHiddenKeys],
  );

  return useMemo(
    () => ({
      columnSizeConfig,
      columnVisibilityConfig,
      onColumnSizeChange,
      columns: finalColumns,
      onColumnOrderChange,
      onColumnVisibilityChange,
      onResetColumnOptions,
      columnOrderConfig,
      exportColumns,
      updateConfigsFrom,
      draggableColumns,
      alwaysVisibleKeys,
    }),
    [
      columnSizeConfig,
      columnVisibilityConfig,
      onResetColumnOptions,
      onColumnSizeChange,
      finalColumns,
      onColumnOrderChange,
      onColumnVisibilityChange,
      columnOrderConfig,
      exportColumns,
      updateConfigsFrom,
      draggableColumns,
      alwaysVisibleKeys,
    ],
  );
}

const getOrder = (key: string): ColumnOrderConfig => {
  return getLocalStorage(`${key}_column_order`, []);
};
const setOrder = (key: string, config: ColumnOrderConfig) => {
  return setLocalStorage(`${key}_column_order`, config);
};
const deleteOrder = (key: string) => {
  return deleteLocalStorage(`${key}_column_order`);
};
const getVisibility = (key: string): ColumnVisibilityConfig => {
  return getLocalStorage(`${key}_column_visibility`);
};
const setVisibility = (key: string, config: ColumnVisibilityConfig) => {
  return setLocalStorage(`${key}_column_visibility`, config);
};
const deleteVisibility = (key: string) => {
  return deleteLocalStorage(`${key}_column_visibility`);
};
const getSize = (key: string): ColumnSizeConfig => {
  return getLocalStorage(`${key}_column_size`);
};
const setSize = (key: string, config: ColumnSizeConfig) => {
  return setLocalStorage(`${key}_column_size`, config);
};
const deleteSize = (key: string) => {
  return deleteLocalStorage(`${key}_column_size`);
};
